// Day-of-week × hour-of-day heatmap. CSS grid (no table, no Recharts)
// so the cells flex to fill whatever box the parent gives us — the
// previous fixed-pixel-cell version left huge dead space inside the
// "When breaches happen" card.
//
// Row 0 = Monday, row 6 = Sunday (matches ISO weekday conventions and
// the operator mental model: "what's our weekend look like?"). The
// presenter ships `cells` with `dow` already converted to 0=Mon..6=Sun,
// or 0=Sun..6=Sat — `dowStart` controls which.
//
// Cells render a tinted background that interpolates from a transparent
// base to a saturated red. Hover shows a tooltip via the `title`
// attribute: "Mon 14:00–14:59: 3 breaches".

type Cell = { dow: number; hour: number; count: number }

type Props = {
  cells: Cell[]
  /** Number of column buckets — 24 raw hours or 6 four-hour bins. */
  bins?: 24 | 6
  /** Day-of-week origin. Defaults to "mon" (ISO). */
  dowStart?: "mon" | "sun"
  /** Hex color for the saturated end of the gradient. */
  color?: string
  emptyMessage?: string
}

const DAYS_MON = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
const DAYS_SUN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// Mock-style 4-hour bin labels. AM/PM format keeps each label short
// enough to render on one line at any reasonable card width.
const SIX_BIN_LABELS = ["12-4a", "4-8a", "8a-12p", "12-4p", "4-8p", "8p-12a"]

// 24-hour mode shows every 3rd label so the labels never crowd. The
// blanks are still rendered as empty cells so spacing stays consistent.
function hourLabelFor24(i: number): string {
  return i % 3 === 0 ? i.toString().padStart(2, "0") : ""
}

export function HeatmapGrid({
  cells,
  bins = 24,
  dowStart = "mon",
  color = "#ef4444",
  emptyMessage = "No breaches in this window yet.",
}: Props) {
  if (!cells || cells.length === 0) {
    return (
      <div className="grid h-full place-items-center text-sm text-app-muted">{emptyMessage}</div>
    )
  }

  const days = dowStart === "mon" ? DAYS_MON : DAYS_SUN
  const cols = bins

  const grid: number[][] = Array.from({ length: 7 }, () => Array(cols).fill(0))
  let max = 0
  for (const c of cells) {
    if (c.dow < 0 || c.dow > 6) continue
    if (c.hour < 0 || c.hour > 23) continue
    const col = bins === 24 ? c.hour : Math.floor(c.hour / 4)
    grid[c.dow][col] = (grid[c.dow][col] ?? 0) + (c.count ?? 0)
    if (grid[c.dow][col] > max) max = grid[c.dow][col]
  }

  if (max === 0) {
    return (
      <div className="grid h-full place-items-center text-sm text-app-muted">{emptyMessage}</div>
    )
  }

  const colLabels: string[] =
    bins === 24 ? Array.from({ length: 24 }, (_, i) => hourLabelFor24(i)) : SIX_BIN_LABELS

  return (
    <div
      className="grid h-full w-full"
      style={{
        gridTemplateColumns: `auto repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `auto repeat(7, minmax(0, 1fr))`,
        columnGap: 3,
        rowGap: 3,
      }}
    >
      {/* Header row: empty corner + column labels */}
      <div aria-hidden />
      {colLabels.map((lbl, i) => (
        <div
          key={`h-${i}`}
          className="text-center text-[10px] font-medium text-app-subtle whitespace-nowrap pb-1"
        >
          {lbl}
        </div>
      ))}

      {/* Body: day-label + 24 (or 6) cells per row */}
      {grid.map((row, rIdx) => (
        <RowFragment
          key={`r-${rIdx}`}
          dayLabel={days[rIdx]}
          counts={row}
          max={max}
          color={color}
          bins={bins}
        />
      ))}
    </div>
  )
}

function RowFragment({
  dayLabel,
  counts,
  max,
  color,
  bins,
}: {
  dayLabel: string
  counts: number[]
  max: number
  color: string
  bins: 24 | 6
}) {
  return (
    <>
      <div className="pr-2 text-right text-[11px] font-medium text-app-subtle self-center whitespace-nowrap">
        {dayLabel}
      </div>
      {counts.map((count, cIdx) => {
        const pct = count / max
        const opacity = count === 0 ? 0.05 : 0.15 + pct * 0.8
        const titleHour =
          bins === 24
            ? `${cIdx.toString().padStart(2, "0")}:00`
            : `${(cIdx * 4).toString().padStart(2, "0")}:00–${((cIdx + 1) * 4).toString().padStart(2, "0")}:00`
        return (
          <div
            key={cIdx}
            title={`${dayLabel} ${titleHour}: ${count} breach${count === 1 ? "" : "es"}`}
            className="rounded-[3px] min-h-[14px]"
            style={{ backgroundColor: hexWithAlpha(color, opacity) }}
          />
        )
      })}
    </>
  )
}

// rrggbb → rgba(r,g,b,a). Falls back to the input string for non-hex
// (e.g. tailwind class names typoed in by callers).
function hexWithAlpha(hex: string, alpha: number): string {
  const m = hex.match(/^#?([0-9a-f]{6})$/i)
  if (!m) return hex
  const v = m[1]
  const r = parseInt(v.slice(0, 2), 16)
  const g = parseInt(v.slice(2, 4), 16)
  const b = parseInt(v.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`
}
